import { motion } from "framer-motion";
import { Instagram, Facebook, Star, ArrowUpRight } from "lucide-react";
import { SOCIAL, GALLERY, CONTACT } from "../../lib/siteData";

const igShots = GALLERY.slice(0, 4);

export const ConnectSection = () => {
  return (
    <section
      id="connect"
      className="relative bg-[#080D0A] py-24 md:py-32 border-t border-[#223026]"
      data-testid="connect-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
        {/* Social */}
        <motion.div
          id="social"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="group relative overflow-hidden border border-[#223026] bg-[#0F150F] p-8 md:p-10 flex flex-col justify-between"
          data-testid="social-cta"
        >
          <div>
            <span className="inline-flex items-center gap-2 text-lime-400 text-sm uppercase tracking-[0.2em] font-bold">
              <Instagram size={18} /> Social
            </span>
            <h3 className="font-display font-bold text-3xl md:text-4xl text-white mt-4 leading-tight tracking-tight">
              Follow the work.
            </h3>
            <p className="text-neutral-400 mt-3 max-w-sm">
              See the latest clears, builds and dirt work — follow along on social.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-4 gap-2">
            {igShots.map((s, i) => (
              <div key={i} className="relative aspect-square overflow-hidden border border-[#223026]">
                <img
                  src={s.url}
                  alt={s.label}
                  loading="lazy"
                  className="w-full h-full object-cover brightness-90 group-hover:brightness-110 transition duration-500"
                />
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a
              href={SOCIAL.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-btn"
              className="group/ig flex-1 inline-flex items-center justify-center gap-2 border border-[#2A3A2E] text-white px-5 py-3.5 text-sm font-semibold hover:border-lime-400 hover:text-lime-400 transition-colors duration-200"
            >
              <Instagram size={17} /> Instagram
              <ArrowUpRight size={15} className="opacity-60 group-hover/ig:opacity-100" />
            </a>
            <a
              href={SOCIAL.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="facebook-btn"
              className="group/fb flex-1 inline-flex items-center justify-center gap-2 border border-[#2A3A2E] text-white px-5 py-3.5 text-sm font-semibold hover:border-lime-400 hover:text-lime-400 transition-colors duration-200"
            >
              <Facebook size={17} /> Facebook
              <ArrowUpRight size={15} className="opacity-60 group-hover/fb:opacity-100" />
            </a>
          </div>
        </motion.div>

        {/* Google review */}
        <motion.div
          id="reviews"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden border border-[#223026] bg-[#0F150F] p-8 md:p-10 flex flex-col justify-between"
          data-testid="google-review-cta"
        >
          <div>
            <span className="inline-flex items-center gap-2 text-lime-400 text-sm uppercase tracking-[0.2em] font-bold">
              <Star size={18} /> Reviews
            </span>
            <h3 className="font-display font-bold text-3xl md:text-4xl text-white mt-4 leading-tight tracking-tight">
              Worked with us?
            </h3>
            <p className="text-neutral-400 mt-3 max-w-sm">
              A quick Google review helps other folks around {CONTACT.location} find
              honest, quality land work.
            </p>
            <div className="flex items-center gap-1 mt-6" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={22} className="text-lime-400 fill-lime-400" />
              ))}
            </div>
          </div>

          <a
            href={SOCIAL.googleReview}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-review-btn"
            className="group mt-8 inline-flex items-center justify-center gap-2 bg-lime-400 text-black font-semibold px-7 py-4 text-sm uppercase tracking-wider hover:bg-white transition-colors duration-200 w-full sm:w-max"
          >
            Leave a Google Review
            <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
